import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { SnackbarService } from '../../services/snackbar.service';
import { DataLoaderComponent } from '../../shared/data-loader/data-loader.component';
import { StationScheduleService } from '../../services/station-schedule.service';
import { isFulfilmentRejected } from '../../utils/payment-errors';

const RETRYABLE = new Set(['FAILED', 'CANCELLED', 'EXPIRED']);
const MAX_ATTEMPTS = 5;

@Component({
  selector: 'app-payment-failure',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    DataLoaderComponent,
  ],
  templateUrl: './payment-failure.component.html',
  styleUrl: './payment-failure.component.css'
})
export class PaymentFailureComponent implements OnInit, OnDestroy {
  sessionIdParam: string | null = null;
  sessionId: number | null = null;
  failureMessage: string | null = null;
  failureCode: string | null = null;
  status: string | null = null;
  attemptNumber: number | null = null;
  amount: number | null = null;
  currency: string = 'USD';
  loading = true;
  /** True while waiting for webhook to possibly flip to FAILED */
  statusPolling = false;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private pollRemaining = 0;
  private destroyed = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private scheduleService: StationScheduleService,
    private snackbar: SnackbarService
  ) {}

  /**
   * Payment captured but the order was rejected during fulfilment (price/inventory/promo
   * changed): the payment is refunded and retrying this session must not be offered.
   */
  get fulfilmentRejected(): boolean {
    return isFulfilmentRejected({ status: this.status, failure_code: this.failureCode });
  }

  get canRetry(): boolean {
    if (this.fulfilmentRejected) return false;
    if (this.loading || this.statusPolling || this.sessionId == null || !this.status) return false;
    if (!RETRYABLE.has(this.status)) return false;
    const n = this.attemptNumber ?? 1;
    return n < MAX_ATTEMPTS;
  }

  ngOnInit() {
    this.sessionIdParam = this.route.snapshot.queryParamMap.get('session_id');
    if (this.sessionIdParam) {
      const id = parseInt(this.sessionIdParam, 10);
      if (!isNaN(id)) {
        this.loadSession(id, true);
        return;
      }
    }
    this.loading = false;
  }

  private loadSession(id: number, initial: boolean) {
    this.scheduleService.getPaymentSession(id).subscribe({
      next: (session) => {
        if (this.destroyed) return;
        this.sessionId = session.id;
        this.failureMessage = session.failure_message || null;
        this.failureCode = session.failure_code || null;
        this.status = session.status || null;
        this.attemptNumber = session.attempt_number ?? null;
        this.amount = session.amount ?? null;
        this.currency = session.currency || 'USD';
        this.loading = false;

        // If the session status is SUCCEEDED (e.g. user navigated here by mistake),
        // redirect to the success page immediately
        if (session.status === 'SUCCEEDED') {
          this.snackbar.info('This payment already completed. Redirecting…', 3000);
          this.router.navigate(['/payment-success'], {
            queryParams: {
              session_id: id,
              ...(session.order_id ? { order_id: String(session.order_id) } : {})
            }
          });
          return;
        }

        // If the session is still in a pre-failure state (webhook not yet fired),
        // redirect to payment-return which handles pending status properly
        if (
          initial &&
          (session.status === 'PAYMENT_INTENT_CREATED' ||
            session.status === 'INITIATED')
        ) {
          // The backend should have healed via Stripe fallback already.
          // If we still got PAYMENT_INTENT_CREATED, the payment may still be processing.
          // Poll a few times to see if it transitions.
          this.startStatusPolling(id);
          return;
        }

        // PROCESSING state — payment still in flight (ACH etc.)
        if (initial && session.status === 'PROCESSING') {
          this.startStatusPolling(id);
          return;
        }
      },
      error: (err) => {
        if (this.destroyed) return;
        this.loading = false;
        this.statusPolling = false;
        const msg =
          err?.error?.message || err?.error?.error || 'Could not load payment details.';
        this.snackbar.error(msg, 5000);
      }
    });
  }

  private startStatusPolling(id: number) {
    this.statusPolling = true;
    this.pollRemaining = 8;
    const tick = () => {
      if (this.destroyed) return;
      if (this.pollRemaining-- <= 0) {
        this.statusPolling = false;
        return;
      }
      this.scheduleService.getPaymentSession(id).subscribe({
        next: (session) => {
          if (this.destroyed) return;
          this.failureMessage = session.failure_message || null;
          this.failureCode = session.failure_code || null;
          this.status = session.status || null;
          this.attemptNumber = session.attempt_number ?? null;
          this.amount = session.amount ?? this.amount;
          this.currency = session.currency || this.currency;

          if (RETRYABLE.has(session.status || '')) {
            // Session is now definitively failed / cancelled / expired
            this.statusPolling = false;
            return;
          }
          if (session.status === 'SUCCEEDED') {
            this.statusPolling = false;
            this.snackbar.info('Payment already completed. Redirecting…', 3000);
            this.router.navigate(['/payment-success'], {
              queryParams: {
                session_id: id,
                ...(session.order_id ? { order_id: String(session.order_id) } : {})
              }
            });
            return;
          }
          this.pollTimer = setTimeout(tick, 2000);
        },
        error: () => {
          if (this.destroyed) return;
          this.pollTimer = setTimeout(tick, 2000);
        }
      });
    };
    this.pollTimer = setTimeout(tick, 1500);
  }

  retryPayment() {
    if (!this.canRetry || this.sessionId == null) return;
    this.router.navigate(['/payment', this.sessionId]);
  }

  formatMoney(amount: number, currency: string): string {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: (currency || 'USD').toUpperCase()
      }).format(amount);
    } catch {
      return `${amount} ${currency}`;
    }
  }

  ngOnDestroy() {
    this.destroyed = true;
    if (this.pollTimer) clearTimeout(this.pollTimer);
  }
}
